import { db } from "./dbClient";

// ─── CTRPage.jsx ──────────────────────────────────────────────────────────────
// Currency Transaction Report (CTR) filing module.
// Manages CTR filings for cash transactions over $10,000.

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { printToPDF, downloadXML, generateCTRXML, printCTRFormPDF, sanitize } from "./exportUtils";
import AuditLogPanel from "./AuditLogPanel";

const EMPTY_FORM = () => ({
  filing_institution: "",
  subject_name: "",
  subject_dob: "",
  subject_address: "",
  subject_id_type: "Passport",
  subject_id_number: "",
  transaction_amount: "",
  transaction_date: "",
  currency: "USD",
  jurisdiction: "",
  account_number: "",
  transaction_purpose: "",
  is_cash_in: true,
  is_business: false,
  status: "draft",
});

const TRANSACTION_PURPOSES = [
  "Business Payment", "Payroll", "Property Purchase", "Loan Repayment",
  "Investment", "Tax Payment", "Personal / Family Support", "Gambling",
  "Trade / Import-Export", "Cryptocurrency Exchange", "Other",
];

const ID_TYPES = ["Passport", "Driver's License", "National ID", "SSN/TIN", "Other"];
const JURISDICTIONS = ["US", "CA", "UK", "AU", "EU", "SG", "AE", "Other"];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "SGD", "AED", "CHF", "JPY"];
const STATUS_COLORS = { draft: "gold", filed: "accent", submitted: "success" };

function statusBadge(status, C) {
  const colorKey = STATUS_COLORS[status] || "textDim";
  const color = C[colorKey] || C.textDim;
  return (
    <span style={{
      background: `${color}20`,
      color,
      border: `1px solid ${color}55`,
      borderRadius: 5,
      padding: "2px 9px",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}>
      {status}
    </span>
  );
}

function daysUntilDeadline(transactionDate) {
  if (!transactionDate) return null;
  const txDate = new Date(transactionDate);
  const today = new Date();
  const diffMs = today - txDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return 15 - diffDays;
}

function DeadlineBadge({ transactionDate, C }) {
  const days = daysUntilDeadline(transactionDate);
  if (days === null) return <span style={{ color: C.textDim }}>—</span>;
  const color = days <= 3 ? C.danger : days <= 7 ? C.warn : C.success;
  return (
    <span style={{ color, fontWeight: 700, fontSize: 12 }}>
      {days < 0 ? "OVERDUE" : `${days}d`}
    </span>
  );
}

function Field({ label, children, span2 }) {
  const C = useTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5,
      gridColumn: span2 ? "span 2" : "span 1" }}>
      <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>
        {label.toUpperCase()}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.surfaceAlt,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    padding: "8px 10px",
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    width: "100%",
    outline: "none",
  };
}

// Helper to get translated option label
function getTranslatedPurpose(purpose, t) {
  const purposeMap = {
    "Business Payment": "ctr.purposes.business",
    "Payroll": "ctr.purposes.payroll",
    "Property Purchase": "ctr.purposes.property",
    "Loan Repayment": "ctr.purposes.loan",
    "Investment": "ctr.purposes.investment",
    "Tax Payment": "ctr.purposes.tax",
    "Personal / Family Support": "ctr.purposes.personal",
    "Gambling": "ctr.purposes.gambling",
    "Trade / Import-Export": "ctr.purposes.trade",
    "Cryptocurrency Exchange": "ctr.purposes.crypto",
    "Other": "ctr.purposes.other",
  };
  return t(purposeMap[purpose] || purpose);
}

function getTranslatedIdType(idType, t) {
  const idTypeMap = {
    "Passport": "ctr.idTypes.passport",
    "Driver's License": "ctr.idTypes.license",
    "National ID": "ctr.idTypes.national",
    "SSN/TIN": "ctr.idTypes.ssn",
    "Other": "ctr.idTypes.other",
  };
  return t(idTypeMap[idType] || idType);
}

function getTranslatedJurisdiction(jurisdiction, t) {
  const jurisdictionMap = {
    "US": "ctr.jurisdictions.us",
    "CA": "ctr.jurisdictions.ca",
    "UK": "ctr.jurisdictions.uk",
    "AU": "ctr.jurisdictions.au",
    "EU": "ctr.jurisdictions.eu",
    "SG": "ctr.jurisdictions.sg",
    "AE": "ctr.jurisdictions.ae",
    "Other": "ctr.jurisdictions.other",
  };
  return t(jurisdictionMap[jurisdiction] || jurisdiction);
}

function getTranslatedCurrency(currency, t) {
  const currencyMap = {
    "USD": "ctr.currencies.usd",
    "EUR": "ctr.currencies.eur",
    "GBP": "ctr.currencies.gbp",
    "CAD": "ctr.currencies.cad",
    "AUD": "ctr.currencies.aud",
    "SGD": "ctr.currencies.sgd",
    "AED": "ctr.currencies.aed",
    "CHF": "ctr.currencies.chf",
    "JPY": "ctr.currencies.jpy",
  };
  return t(currencyMap[currency] || currency);
}

export default function CTRPage({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);

  const [ctrs, setCtrs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM());
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState("");
  const [customers, setCustomers] = useState([]);
  // E-007: Aggregation warning state
  const [aggregationWarning, setAggregationWarning] = useState(null);
  // E-008: Exemption management — persisted to ctr_exemptions Supabase table
  const [exemptions, setExemptions] = useState([]);
  const [showExemptions, setShowExemptions] = useState(false);
  // F-011 equivalent for CTR: acknowledge overdue breach before filing
  const [ctrOverdueAcknowledged, setCtrOverdueAcknowledged] = useState(false);

  useEffect(() => {
    loadCTRs();
    loadExemptions();
    loadCustomers();
  }, [company?.id]);

  // Auto-populate account number when subject name changes
  useEffect(() => {
    async function lookupAccountNumber() {
      if (!form.subject_name || !company?.id) {
        console.log('[CTR] Skipping lookup: missing subject_name or company_id');
        return;
      }
      console.log('[CTR] Looking up account number for:', form.subject_name);
      try {
        const subjectName = form.subject_name.trim();

        // Try to find matching customer in the customers list first (most reliable)
        let matchedCustomer = customers.find(c => {
          // Use same display name logic as datalist
          let name = "";
          if (c.business_name && c.business_name.trim()) {
            name = c.business_name;
          } else if (c.full_name && c.full_name.trim()) {
            name = c.full_name;
          } else {
            const personalName = [c.first_name, c.last_name].filter(Boolean).join(" ");
            name = personalName || "";
          }
          return name.toLowerCase() === subjectName.toLowerCase();
        });

        if (matchedCustomer) {
          console.log('[CTR] Found customer from list:', matchedCustomer);
          const accountNum = matchedCustomer.account_number;
          if (accountNum && form.account_number !== accountNum) {
            console.log('[CTR] Setting account number from customer list:', accountNum);
            setForm(f => ({ ...f, account_number: accountNum }));
          } else if (!accountNum) {
            console.log('[CTR] Customer found but has no account number');
          }
        } else {
          console.log('[CTR] Customer not found in list, trying database search');
          // Fallback: try database search with flexible matching
          const { data: kycData } = await db.from("kyc_submissions")
            .select("id, account_number, full_name, first_name, last_name, business_name")
            .eq("company_id", company.id)
            .eq("status", "approved")
            .order("created_at", { ascending: false })
            .limit(10)
            .execute();

          const found = kycData?.find(rec => {
            const names = [
              rec.full_name,
              [rec.first_name, rec.last_name].filter(Boolean).join(" "),
              rec.business_name
            ].filter(Boolean);
            return names.some(n => n.toLowerCase().includes(subjectName.toLowerCase()));
          });

          if (found) {
            console.log('[CTR] Found KYC record:', found);
            const accountNum = found.account_number;
            if (accountNum && form.account_number !== accountNum) {
              console.log('[CTR] Setting account number:', accountNum);
              setForm(f => ({ ...f, account_number: accountNum }));
            }
          } else {
            console.log('[CTR] No KYC submission found for:', subjectName);
          }
        }
      } catch (e) {
        console.error('[CTR] Failed to lookup account number:', e);
      }
    }
    lookupAccountNumber();
  }, [form.subject_name, company?.id, customers]);

  async function loadCustomers() {
    if (!company?.id) return;
    try {
      const { data } = await db.from("kyc_submissions")
        .select("id, first_name, last_name, business_name, account_number, full_name")
        .eq("company_id", company.id)
        .eq("status", "approved")
        .order("submitted_at", { ascending: false })
        .limit(500)
        .execute();
      console.log('[CTR] Loaded customers:', data?.length || 0);
      setCustomers(data || []);
    } catch (e) {
      console.warn('[CTR] Failed to load customers:', e);
    }
  }

  async function loadExemptions() {
    if (!company?.id) return;
    const { data } = await db.from("ctr_exemptions")
      .select("*").eq("company_id", company.id).order("added_at", true).execute();
    setExemptions(data || []);
  }

  async function loadCTRs() {
    if (!company?.id) {
      console.log('[CTRPage] No company ID, skipping load');
      return;
    }
    setLoading(true);
    console.log('[CTRPage] Loading CTRs for company:', company.id);
    const { data, error } = await db
      .from("ctr_filings")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", false).execute();
    console.log('[CTRPage] Query result:', { data, error });
    setCtrs(data || []);
    setLoading(false);
  }

  function openNew() {
    setForm({
      ...EMPTY_FORM(),
      filing_institution: company?.name || "",
    });
    setEditId(null);
    setError(null);
    setShowForm(true);
  }

  function openEdit(ctr) {
    setForm({
      filing_institution: ctr.filing_institution || "",
      subject_name: ctr.subject_name || "",
      subject_dob: ctr.subject_dob || "",
      subject_address: ctr.subject_address || "",
      subject_id_type: ctr.subject_id_type || "Passport",
      subject_id_number: ctr.subject_id_number || "",
      transaction_amount: ctr.transaction_amount || "",
      transaction_date: ctr.transaction_date || "",
      currency: ctr.currency || "USD",
      jurisdiction: ctr.jurisdiction || "",
      account_number: ctr.account_number || "",
      transaction_purpose: ctr.transaction_purpose || "",
      is_cash_in: ctr.is_cash_in ?? true,
      is_business: ctr.is_business ?? false,
      status: ctr.status || "draft",
    });
    setEditId(ctr.id);
    setError(null);
    setShowForm(true);
  }

  // E-008: Add/remove exemptions in Supabase ctr_exemptions table
  async function addExemption(name, type) {
    if (!company?.id || !name) return;
    const { data } = await db.from("ctr_exemptions").insert({
      company_id: company.id,
      subject_name: name,
      exemption_type: type,
      added_by: currentUser?.email,
    }).execute();
    if (data && Array.isArray(data) && data[0]) setExemptions(prev => [...prev, data[0]]);
  }

  async function removeExemption(id) {
    await db.from("ctr_exemptions").delete().eq("id", id).execute();
    setExemptions(prev => prev.filter(e => e.id !== id));
  }

  function isExempt(subjectName) {
    return exemptions.some(e => (e.subject_name || e.name || "").toLowerCase() === subjectName?.toLowerCase().trim());
  }

  // E-007 / F-015: Check same-subject same-day aggregation
  async function checkAggregation() {
    if (!form.subject_name || !form.transaction_date || !company?.id) return false;
    const { data } = await db.from("ctr_filings")
      .select("id, subject_name, transaction_date, transaction_amount, currency, status")
      .eq("company_id", company.id)
      .eq("transaction_date", form.transaction_date)
      .execute();

    if (!data?.length) return false;

    // Filter in JavaScript for ilike and neq
    const filtered = data.filter(d =>
      d.id !== editId &&
      d.status !== "void" &&
      (d.subject_name || "").toLowerCase().includes(form.subject_name.trim().toLowerCase())
    );

    if (filtered.length > 0) {
      const total = filtered.reduce((sum, d) => sum + (parseFloat(d.transaction_amount) || 0), 0)
        + (parseFloat(form.transaction_amount) || 0);
      if (total >= 10000) {
        setAggregationWarning({ total, others: filtered });
        return true;
      }
    }
    return false;
  }

  async function performSave(status) {
    if (!form.subject_name || !form.transaction_amount || !form.transaction_date) {
      setError("Subject Name, Amount, and Transaction Date are required.");
      return;
    }
    // E-008: Warn if subject is exempt
    if (isExempt(form.subject_name)) {
      setError(`⚠ ${form.subject_name} is on the CTR exemption list. Verify exemption applies before filing.`);
    }
    // #14: Watchlist check at CTR entry point
    const { data: allWL } = await db.from("internal_watchlist")
      .select("full_name, list_type, reason")
      .eq("company_id", company.id)
      .execute();

    const wlHits = (allWL || []).filter(w =>
      (w.full_name || "").toLowerCase().includes(form.subject_name.trim().toLowerCase())
    ).slice(0, 3);

    if (wlHits && wlHits.length > 0) {
      const hitStr = wlHits.map(h => `${h.full_name} (${h.list_type})`).join(", ");
      setError(`🚨 WATCHLIST HIT: ${hitStr} — Review required before proceeding.`);
      return;
    }
    // E-007: Check same-subject same-day aggregation (only for new filed CTRs)
    if (!editId && status !== "draft") {
      const isAgg = await checkAggregation();
      if (isAgg) return;
    }
    setSaving(true);
    setError(null);
    // E-041: Sanitize free-text fields before persisting
    const payload = {
      ...form,
      status: status,
      filing_institution:  sanitize(form.filing_institution),
      subject_name:        sanitize(form.subject_name),
      subject_address:     sanitize(form.subject_address),
      subject_id_number:   sanitize(form.subject_id_number),
      account_number:      sanitize(form.account_number),
      transaction_purpose: sanitize(form.transaction_purpose),
      company_id: company.id,
      transaction_amount: parseFloat(form.transaction_amount) || 0,
      updated_at: new Date().toISOString(),
    };
    let result;
    if (editId) {
      result = await db.from("ctr_filings").update(payload).eq("id", editId).execute();
    } else {
      result = await db.from("ctr_filings").insert({
        ...payload,
        created_by: currentUser?.email,
        created_at: new Date().toISOString(),
        filed_date: (status === "filed" || status === "submitted")
          ? new Date().toISOString().slice(0, 10) : null,
      }).execute();
    }

    const { data, error } = result;
    if (error) {
      setError(error.message || "Save failed");
      setSaving(false);
      return;
    }
    const recordId = editId || (Array.isArray(data) && data[0]?.id);
    await log({
      action: editId ? "ctr_updated" : "ctr_created",
      module: "ctr",
      entityId: recordId,
      entityName: form.subject_name,
      newValue: { status: status, amount: form.transaction_amount },
    });
    await loadCTRs();
    setShowForm(false);
    setSaving(false);
  }

  async function handleSaveDraft() {
    await performSave("draft");
  }

  async function handleFileCTR() {
    await performSave("filed");
  }

  async function handleFile(ctr) {
    const filedDate = new Date().toISOString().slice(0, 10);
    // Update local state immediately to show filed date right away
    setCtrs(prev => prev.map(c => c.id === ctr.id
      ? { ...c, status: "filed", filed_date: filedDate, updated_at: new Date().toISOString() }
      : c
    ));

    try {
      const { data, error: err } = await db.from("ctr_filings")
        .update({
          status: "filed",
          filed_date: filedDate,
          updated_at: new Date().toISOString(),
        })
        .eq("id", ctr.id)
        .select()
        .single()
        .execute();

      if (err) {
        console.error('[CTR] File update error:', err);
        await loadCTRs();
        return;
      }

      console.log('[CTR] Filed successfully:', data);
      if (ctr.id) {
        try {
          await log({
            action: "ctr_filed",
            module: "ctr",
            entityId: ctr.id,
            entityName: ctr.subject_name,
            newValue: { status: "filed", filed_date: filedDate },
          });
        } catch (logErr) {
          console.warn('[CTR] Audit log failed:', logErr);
        }
      }
    } catch (e) {
      console.error('[CTR] File update exception:', e);
      await loadCTRs();
    }
  }

  function handleDownloadPDF(ctr) {
    const content = `
      <div class="section">
        <div class="section-title">Currency Transaction Report</div>
        <table>
          <tbody>
            <tr><td><strong>Filing Institution</strong></td><td>${ctr.filing_institution || "—"}</td></tr>
            <tr><td><strong>Subject Name</strong></td><td>${ctr.subject_name || "—"}</td></tr>
            <tr><td><strong>Subject DOB</strong></td><td>${ctr.subject_dob || "—"}</td></tr>
            <tr><td><strong>Subject Address</strong></td><td>${ctr.subject_address || "—"}</td></tr>
            <tr><td><strong>ID Type / Number</strong></td><td>${ctr.subject_id_type || "—"} / ${ctr.subject_id_number || "—"}</td></tr>
            <tr><td><strong>Account Number</strong></td><td>${ctr.account_number || "—"}</td></tr>
            <tr><td><strong>Transaction Amount</strong></td><td>${ctr.currency || "USD"} ${Number(ctr.transaction_amount || 0).toLocaleString()}</td></tr>
            <tr><td><strong>Transaction Date</strong></td><td>${ctr.transaction_date || "—"}</td></tr>
            <tr><td><strong>Purpose of Transaction</strong></td><td>${ctr.transaction_purpose || "—"}</td></tr>
            <tr><td><strong>Direction</strong></td><td>${ctr.is_cash_in ? "Cash In" : "Cash Out"}</td></tr>
            <tr><td><strong>Subject Type</strong></td><td>${ctr.is_business ? "Business" : "Individual"}</td></tr>
            <tr><td><strong>Jurisdiction</strong></td><td>${ctr.jurisdiction || "—"}</td></tr>
            <tr><td><strong>Status</strong></td><td>${ctr.status || "draft"}</td></tr>
            <tr><td><strong>Filed Date</strong></td><td>${ctr.filed_date || "—"}</td></tr>
            <tr><td><strong>Filed Ref</strong></td><td>${ctr.filed_ref || "—"}</td></tr>
          </tbody>
        </table>
      </div>
    `;
    printToPDF(`CTR — ${ctr.subject_name}`, content, company?.name);
  }

  function handleBSAXML() {
    const xml = generateCTRXML(form, company);
    downloadXML(xml, `CTR_BSA_${(form.subject_name || "subject").replace(/\s+/g,"_")}_${Date.now()}.xml`);
  }

  function handleOfficialPDF() {
    printCTRFormPDF(form, company);
  }

  const filtered = ctrs.filter(c =>
    !search || c.subject_name?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Form modal ─────────────────────────────────────────────────────────────
  if (showForm) {
    return (
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button
            onClick={() => setShowForm(false)}
            style={{ background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 6, color: C.textMid, padding: "6px 14px",
              cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
            ← Back
          </button>
          <h2 style={{ fontFamily: "'Syne', sans-serif", color: C.text,
            fontSize: 20, fontWeight: 700, margin: 0 }}>
            {editId ? "Edit CTR Filing" : "New CTR Filing"}
          </h2>
        </div>

        {/* Compliance banner */}
        <div style={{ background: `${C.warn}15`, border: `1px solid ${C.warn}55`,
          borderRadius: 8, padding: "10px 16px", marginBottom: 24,
          display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <span style={{ color: C.warn, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
            CTRs must be filed within 15 days of the triggering transaction.
            Cash transactions over $10,000 require mandatory CTR filing.
          </span>
        </div>

        {/* Filing guidance banner */}
        <div style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}30`,
          borderRadius: 8, padding: "10px 16px", marginBottom: 20,
          display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
          <div>
            <div style={{ color: C.text, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Direct Filing Options
            </div>
            <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>
              <strong>US (FinCEN):</strong> Download BSA XML → upload to{" "}
              <span style={{ color: C.accent }}>bsaefiling.fincen.treas.gov</span>
              {" · "}
              CTRs must be filed within <strong>15 days</strong> of the transaction
              {" · "}
              <strong>All other jurisdictions:</strong> Use Official Form 112 PDF → submit to your regulator
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: `${C.danger}15`, border: `1px solid ${C.danger}55`,
            borderRadius: 7, padding: "10px 14px", marginBottom: 16,
            color: C.danger, fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: 28 }}>

          {/* Section: Institution */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.12em",
              marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
              FILING INSTITUTION
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Filing Institution" span2>
                <input
                  value={form.filing_institution}
                  onChange={e => setForm(f => ({ ...f, filing_institution: e.target.value }))}
                  placeholder="e.g. First National Bank"
                  style={inputStyle(C)}
                />
              </Field>
            </div>
          </div>

          {/* Section: Transaction Direction */}
          <div style={{ marginBottom: 24, background: `${C.accent}08`, border: `2px solid ${C.accent}33`,
            borderRadius: 10, padding: 16 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
              ❓ Is this Cash In or Cash Out?
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setForm(f => ({ ...f, is_cash_in: true }))}
                style={{
                  flex: 1, padding: "14px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  fontFamily: "'DM Mono', monospace", cursor: "pointer",
                  background: form.is_cash_in ? C.accent : "transparent",
                  border: `2px solid ${form.is_cash_in ? C.accent : C.border}`,
                  color: form.is_cash_in ? C.bg : C.text,
                  transition: "all 0.2s",
                }}>
                💰 Cash In
              </button>
              <button
                onClick={() => setForm(f => ({ ...f, is_cash_in: false }))}
                style={{
                  flex: 1, padding: "14px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  fontFamily: "'DM Mono', monospace", cursor: "pointer",
                  background: !form.is_cash_in ? `${C.warn}40` : "transparent",
                  border: `2px solid ${!form.is_cash_in ? C.warn : C.border}`,
                  color: !form.is_cash_in ? C.warn : C.text,
                  transition: "all 0.2s",
                }}>
                💸 Cash Out
              </button>
            </div>
          </div>

          {/* Section: Subject */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.12em",
              marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
              SUBJECT INFORMATION
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Subject Name">
                <input
                  list="customers-list"
                  value={form.subject_name}
                  onChange={e => setForm(f => ({ ...f, subject_name: e.target.value }))}
                  placeholder="Type or select from customers…"
                  style={inputStyle(C)}
                />
                <datalist id="customers-list">
                  {customers.map(c => {
                    // For business customers, prioritize business_name
                    // For individuals, use full_name or constructed personal name
                    let name = "Customer";

                    // If business_name exists and is not empty, use it
                    if (c.business_name && c.business_name.trim()) {
                      name = c.business_name;
                    } else if (c.full_name && c.full_name.trim()) {
                      // Otherwise use full_name
                      name = c.full_name;
                    } else {
                      // Fall back to constructed personal name
                      const personalName = [c.first_name, c.last_name].filter(Boolean).join(" ");
                      name = personalName || "Customer";
                    }

                    return <option key={c.id} value={name} />;
                  })}
                </datalist>
              </Field>
              <Field label="Date of Birth">
                <input
                  type="date"
                  value={form.subject_dob}
                  onChange={e => setForm(f => ({ ...f, subject_dob: e.target.value }))}
                  style={inputStyle(C)}
                />
              </Field>
              <Field label="Subject Address" span2>
                <input
                  value={form.subject_address}
                  onChange={e => setForm(f => ({ ...f, subject_address: e.target.value }))}
                  placeholder="Street, City, State, ZIP"
                  style={inputStyle(C)}
                />
              </Field>
              <Field label="ID Type">
                <select
                  value={form.subject_id_type}
                  onChange={e => setForm(f => ({ ...f, subject_id_type: e.target.value }))}
                  style={inputStyle(C)}>
                  {ID_TYPES.map(idType => <option key={idType} value={idType}>{getTranslatedIdType(idType, t)}</option>)}
                </select>
              </Field>
              <Field label="ID Number">
                <input
                  value={form.subject_id_number}
                  onChange={e => setForm(f => ({ ...f, subject_id_number: e.target.value }))}
                  placeholder="ID / document number"
                  style={inputStyle(C)}
                />
              </Field>
            </div>
          </div>

          {/* Section: Transaction */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.12em",
              marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
              TRANSACTION DETAILS
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Transaction Amount">
                <input
                  type="number"
                  value={form.transaction_amount}
                  onChange={e => setForm(f => ({ ...f, transaction_amount: e.target.value }))}
                  placeholder="0.00"
                  min="0"
                  style={inputStyle(C)}
                />
              </Field>
              <Field label="Transaction Date">
                <input
                  type="date"
                  value={form.transaction_date}
                  onChange={e => setForm(f => ({ ...f, transaction_date: e.target.value }))}
                  style={inputStyle(C)}
                />
              </Field>
              <Field label="Currency">
                <select
                  value={form.currency}
                  onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                  style={inputStyle(C)}>
                  {CURRENCIES.map(currency => <option key={currency} value={currency}>{getTranslatedCurrency(currency, t)}</option>)}
                </select>
              </Field>
              <Field label="Account Number">
                <input
                  value={form.account_number}
                  onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))}
                  placeholder="Account number"
                  style={inputStyle(C)}
                />
              </Field>
              <Field label="Purpose of Transaction" span2>
                <select
                  value={form.transaction_purpose}
                  onChange={e => setForm(f => ({ ...f, transaction_purpose: e.target.value }))}
                  style={inputStyle(C)}>
                  <option value="">Select purpose…</option>
                  {TRANSACTION_PURPOSES.map(purpose => <option key={purpose} value={purpose}>{getTranslatedPurpose(purpose, t)}</option>)}
                </select>
              </Field>
              <Field label="Jurisdiction">
                <select
                  value={form.jurisdiction}
                  onChange={e => setForm(f => ({ ...f, jurisdiction: e.target.value }))}
                  style={inputStyle(C)}>
                  <option value="">Select jurisdiction...</option>
                  {JURISDICTIONS.map(jurisdiction => <option key={jurisdiction} value={jurisdiction}>{getTranslatedJurisdiction(jurisdiction, t)}</option>)}
                </select>
              </Field>
            </div>

            {/* Toggles */}
            <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
              {/* Business / Individual */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: C.textMid, fontSize: 11 }}>Subject Type:</span>
                <button
                  onClick={() => setForm(f => ({ ...f, is_business: false }))}
                  style={{
                    padding: "5px 14px", borderRadius: "6px 0 0 6px", fontSize: 11,
                    background: !form.is_business ? C.accentGlow : "transparent",
                    border: `1px solid ${!form.is_business ? C.accent : C.border}`,
                    color: !form.is_business ? C.accent : C.textMid,
                    cursor: "pointer", fontFamily: "'DM Mono', monospace",
                  }}>
                  Individual
                </button>
                <button
                  onClick={() => setForm(f => ({ ...f, is_business: true }))}
                  style={{
                    padding: "5px 14px", borderRadius: "0 6px 6px 0", fontSize: 11,
                    background: form.is_business ? C.accentGlow : "transparent",
                    border: `1px solid ${form.is_business ? C.accent : C.border}`,
                    color: form.is_business ? C.accent : C.textMid,
                    cursor: "pointer", fontFamily: "'DM Mono', monospace",
                    marginLeft: -1,
                  }}>
                  Business
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* Filing export buttons */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginRight: "auto" }}>
              <span style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em" }}>EXPORT FOR FILING:</span>
              <button onClick={handleBSAXML}
                style={{ background: `${C.accent}15`, border: `1px solid ${C.accent}55`,
                  borderRadius: 6, color: C.accent, padding: "6px 12px",
                  cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace",
                  display: "flex", alignItems: "center", gap: 5 }}>
                ⬇ BSA XML (FinCEN)
              </button>
              <button onClick={handleOfficialPDF}
                style={{ background: `${C.gold}15`, border: `1px solid ${C.gold}55`,
                  borderRadius: 6, color: C.gold, padding: "6px 12px",
                  cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace",
                  display: "flex", alignItems: "center", gap: 5 }}>
                📄 Official Form 112 PDF
              </button>
            </div>
            <button
              onClick={() => setShowForm(false)}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 7, color: C.textMid, padding: "9px 22px",
                cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
              Cancel
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              style={{ background: "transparent", border: `1px solid ${C.accent}`,
                borderRadius: 7, color: C.accent, padding: "9px 18px",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 600, fontFamily: "'DM Mono', monospace",
                opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving..." : "💾 Save as Draft"}
            </button>
            <button
              onClick={handleFileCTR}
              disabled={saving}
              style={{ background: C.success, border: "none", borderRadius: 7,
                color: C.bg, padding: "9px 20px", cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                opacity: saving ? 0.6 : 1 }}>
              {saving ? "Filing..." : editId ? "Update & File" : "✓ File CTR"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Table view ─────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", color: C.text,
            fontSize: 24, fontWeight: 700, margin: 0 }}>
            CTR Filing
          </h1>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 4 }}>
            Currency Transaction Reports — cash transactions over $10,000
          </div>
        </div>
        <button
          onClick={openNew}
          style={{ background: C.accent, border: "none", borderRadius: 7,
            color: C.bg, padding: "9px 20px", cursor: "pointer",
            fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
          + New CTR
        </button>
      </div>

      {/* Compliance banner */}
      <div style={{ background: `${C.warn}15`, border: `1px solid ${C.warn}55`,
        borderRadius: 8, padding: "10px 16px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 15 }}>⚠</span>
        <span style={{ color: C.warn, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
          CTRs must be filed within 15 days of the triggering transaction.
        </span>
      </div>

      {/* CTR overdue blocking banner — mirrors SAR F-011 enforcement */}
      {(() => {
        const overdueDrafts = ctrs.filter(c =>
          c.status === "draft" && daysUntilDeadline(c.transaction_date) !== null && daysUntilDeadline(c.transaction_date) < 0
        );
        if (overdueDrafts.length === 0) return null;
        return (
          <div style={{ background:`${C.danger}10`, border:`2px solid ${C.danger}`,
            borderRadius:8, padding:"14px 18px", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ fontSize:18 }}>🚨</span>
              <span style={{ fontWeight:700, fontSize:13, color:C.danger }}>
                OVERDUE CTR{overdueDrafts.length > 1 ? "S" : ""} — Filing Deadline Breached
              </span>
            </div>
            <div style={{ color:C.textMid, fontSize:12, lineHeight:1.6, marginBottom:10 }}>
              {overdueDrafts.length} draft CTR{overdueDrafts.length > 1 ? "s have" : " has"} exceeded the mandatory 15-day filing deadline
              (31 CFR 1010.306). Filing actions are <strong>blocked</strong> until a Compliance Officer acknowledges this breach.
            </div>
            <div style={{ color:C.textDim, fontSize:11, marginBottom:10 }}>
              {overdueDrafts.map(c => `${c.subject_name} — ${Math.abs(daysUntilDeadline(c.transaction_date))} days overdue`).join(" · ")}
            </div>
            {!ctrOverdueAcknowledged ? (
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <button onClick={async () => {
                  setCtrOverdueAcknowledged(true);
                  await log({
                    action: "ctr_overdue_acknowledged",
                    module: "ctr",
                    meta: { overdue_count: overdueDrafts.length, acknowledged_by: currentUser?.id },
                  });
                }} style={{ padding:"8px 18px", background:C.danger, border:"none", borderRadius:7,
                  color:"#fff", cursor:"pointer", fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                  ✓ I Acknowledge This Breach
                </button>
                <span style={{ color:C.textDim, fontSize:11 }}>Acknowledgment is logged to the audit trail.</span>
              </div>
            ) : (
              <div style={{ color:C.warn, fontSize:12, fontWeight:600 }}>
                ✓ Breach acknowledged — file the overdue CTR(s) immediately and notify your BSA Officer.
              </div>
            )}
          </div>
        );
      })()}

      {/* E-007: Aggregation warning banner */}
      {aggregationWarning && (
        <div style={{ background:`${C.warn}15`, border:`1px solid ${C.warn}55`,
          borderRadius:8, padding:"12px 16px", marginBottom:16, color:C.warn, fontSize:12 }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>
            ⚠ Same-Subject Same-Day Aggregation Alert (E-007 / F-015)
          </div>
          <div style={{ color:C.textMid, marginBottom:6 }}>
            <strong>{form.subject_name}</strong> already has CTR(s) on <strong>{form.transaction_date}</strong>.
            Combined total: <strong>${aggregationWarning.total.toLocaleString()}</strong> — exceeds $10,000 threshold.
          </div>
          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <button onClick={() => { setAggregationWarning(null); setSaving(false); handleSave(); }}
              style={{ background:"transparent", border:`1px solid ${C.warn}`, borderRadius:5,
                color:C.warn, fontSize:11, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit" }}>
              Save Anyway
            </button>
            <button onClick={() => setAggregationWarning(null)}
              style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:5,
                color:C.textMid, fontSize:11, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* E-008: CTR Exemption Management */}
      <div style={{ display:"flex", gap:10, marginBottom:16, alignItems:"center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by subject name..."
          style={{ ...inputStyle(C), maxWidth: 300 }}
        />
        <button onClick={() => setShowExemptions(v => !v)}
          style={{ padding:"7px 14px", borderRadius:6, cursor:"pointer", fontSize:11,
            background: showExemptions ? `${C.accent}20` : "transparent",
            border:`1px solid ${showExemptions ? C.accent : C.border}`,
            color: showExemptions ? C.accent : C.textMid, fontFamily:"'DM Mono',monospace" }}>
          🛡 Exemptions ({exemptions.length})
        </button>
      </div>
      {showExemptions && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.accent, marginBottom:10 }}>
            CTR Phase I / Phase II Exemptions (31 CFR 1020.315)
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <input id="exempt-input" placeholder="Subject name to exempt..."
              style={{ ...inputStyle(C), maxWidth:260 }} />
            <select id="exempt-type-input" style={{ ...inputStyle(C), maxWidth:150 }}>
              <option value="Phase I">Phase I (Banks)</option>
              <option value="Phase II">Phase II (Business)</option>
            </select>
            <button onClick={() => {
              const name = document.getElementById("exempt-input").value.trim();
              const type = document.getElementById("exempt-type-input").value;
              if (!name) return;
              addExemption(name, type);
              document.getElementById("exempt-input").value = "";
            }} style={{ background:C.accent, border:"none", borderRadius:6, color:C.bg,
              padding:"7px 14px", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
              + Add
            </button>
          </div>
          {exemptions.length === 0 ? (
            <div style={{ color:C.textDim, fontSize:12 }}>No exemptions configured.</div>
          ) : exemptions.map((e) => (
            <div key={e.id || e.subject_name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"6px 0", borderBottom:`1px solid ${C.border}`, fontSize:12 }}>
              <span><strong>{e.subject_name || e.name}</strong> · {e.exemption_type || e.type} · Added {e.added_at || e.added}</span>
              <button onClick={() => removeExemption(e.id)}
                style={{ background:"transparent", border:"none", color:C.danger,
                  fontSize:12, cursor:"pointer" }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, overflow: "hidden" }}>
        {/* Table header */}
        <div style={{ display: "grid",
          gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.9fr 1fr 0.8fr 0.8fr",
          padding: "10px 16px", borderBottom: `1px solid ${C.border}`,
          background: C.surfaceAlt }}>
          {["Subject Name", "Amount", "Tx Date", "Filed Date", "Status",
            "Jurisdiction", "Deadline", ""].map((h, i) => (
            <div key={i} style={{ color: C.textDim, fontSize: 10,
              letterSpacing: "0.1em", fontWeight: 600 }}>
              {h.toUpperCase()}
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: "40px 0", textAlign: "center",
            color: C.textDim, fontSize: 13 }}>Loading CTRs...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px 0", textAlign: "center" }}>
            <div style={{ color: C.textDim, fontSize: 13, marginBottom: 8 }}>
              {search ? "No CTRs match your search." : "No CTR filings yet."}
            </div>
            {!search && (
              <button onClick={openNew}
                style={{ background: "transparent", border: `1px solid ${C.border}`,
                  borderRadius: 6, color: C.accent, padding: "7px 16px",
                  cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                Create first CTR
              </button>
            )}
          </div>
        ) : (
          filtered.map((ctr, idx) => (
            <div key={ctr.id} style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.9fr 1fr 0.8fr 0.8fr",
              padding: "12px 16px",
              borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}` : "none",
              alignItems: "center",
              background: idx % 2 === 0 ? "transparent" : `${C.surfaceAlt}66`,
              transition: "background 0.1s",
            }}>
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>
                  {ctr.subject_name || "—"}
                </div>
                <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                  {ctr.is_business ? "Business" : "Individual"}
                  {ctr.is_cash_in !== undefined && ` · ${ctr.is_cash_in ? "Cash In" : "Cash Out"}`}
                </div>
              </div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>
                {ctr.currency || "USD"} {Number(ctr.transaction_amount || 0).toLocaleString()}
              </div>
              <div style={{ color: C.textMid, fontSize: 12 }}>
                {ctr.transaction_date || "—"}
              </div>
              <div style={{ color: C.textMid, fontSize: 12 }}>
                {ctr.filed_date || "—"}
              </div>
              <div>{statusBadge(ctr.status || "draft", C)}</div>
              <div style={{ color: C.textMid, fontSize: 12 }}>
                {ctr.jurisdiction || "—"}
              </div>
              <div>
                <DeadlineBadge transactionDate={ctr.transaction_date} C={C} />
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                  onClick={() => openEdit(ctr)}
                  title="Edit"
                  style={{ background: "transparent", border: `1px solid ${C.border}`,
                    borderRadius: 5, color: C.textMid, padding: "4px 9px",
                    cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                  Edit
                </button>
                {ctr.status === "draft" && (() => {
                  const isOverdue = daysUntilDeadline(ctr.transaction_date) !== null && daysUntilDeadline(ctr.transaction_date) < 0;
                  const blocked = isOverdue && !ctrOverdueAcknowledged;
                  return (
                    <button
                      onClick={() => handleFile(ctr)}
                      disabled={blocked}
                      title={blocked ? "Acknowledge the overdue breach above before filing" : "Mark as Filed"}
                      style={{ background: "transparent", border: `1px solid ${blocked ? C.danger + "55" : C.accent + "66"}`,
                        borderRadius: 5, color: blocked ? C.danger : C.accent, padding: "4px 9px",
                        cursor: blocked ? "not-allowed" : "pointer", fontSize: 11,
                        fontFamily: "'DM Mono', monospace", opacity: blocked ? 0.45 : 1 }}>
                      File
                    </button>
                  );
                })()}
                {/* E-009: Amend button for filed CTRs */}
                {(ctr.status === "filed" || ctr.status === "submitted") && (
                  <button
                    onClick={() => {
                      setForm({
                        filing_institution: ctr.filing_institution || "",
                        subject_name: ctr.subject_name || "",
                        subject_dob: ctr.subject_dob || "",
                        subject_address: ctr.subject_address || "",
                        subject_id_type: ctr.subject_id_type || "Passport",
                        subject_id_number: ctr.subject_id_number || "",
                        transaction_amount: ctr.transaction_amount || "",
                        transaction_date: ctr.transaction_date || "",
                        currency: ctr.currency || "USD",
                        jurisdiction: ctr.jurisdiction || "",
                        account_number: ctr.account_number || "",
                        transaction_purpose: ctr.transaction_purpose || "",
                        is_cash_in: ctr.is_cash_in ?? true,
                        is_business: ctr.is_business ?? false,
                        status: "draft",
                        is_amendment: true,
                        original_ctr_id: ctr.id,
                      });
                      setEditId(null);
                      setError(`Amendment of CTR filed ${ctr.filed_date || "unknown date"}. Original ref: ${ctr.filed_ref || ctr.id.slice(0,8)}`);
                      setShowForm(true);
                    }}
                    title="Amend this CTR"
                    style={{ background: "transparent", border: `1px solid ${C.warn}55`,
                      borderRadius: 5, color: C.warn, padding: "4px 9px",
                      cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                    ✎ Amend
                  </button>
                )}
                <button
                  onClick={() => handleDownloadPDF(ctr)}
                  title="Download PDF"
                  style={{ background: "transparent", border: `1px solid ${C.border}`,
                    borderRadius: 5, color: C.textMid, padding: "4px 7px",
                    cursor: "pointer", fontSize: 11 }}>
                  ↓
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats bar */}
      {ctrs.length > 0 && (
        <div style={{ display: "flex", gap: 20, marginTop: 16, flexWrap: "wrap" }}>
          {[
            { label: "Total", value: ctrs.length, color: C.textMid },
            { label: "Draft", value: ctrs.filter(c => c.status === "draft").length, color: C.gold },
            { label: "Filed", value: ctrs.filter(c => c.status === "filed").length, color: C.accent },
            { label: "Submitted", value: ctrs.filter(c => c.status === "submitted").length, color: C.success },
            {
              label: "Overdue",
              value: ctrs.filter(c => {
                const d = daysUntilDeadline(c.transaction_date);
                return d !== null && d < 0 && c.status === "draft";
              }).length,
              color: C.danger
            },
          ].map(s => (
            <div key={s.label} style={{ background: C.surface,
              border: `1px solid ${C.border}`, borderRadius: 7,
              padding: "8px 16px", display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ color: s.color, fontWeight: 700, fontSize: 18 }}>{s.value}</span>
              <span style={{ color: C.textDim, fontSize: 11 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <AuditLogPanel company={company} module="ctr" />
    </div>
  );
}
